import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Dropdown,
  Menu,
  Space,
  Input,
  Checkbox,
  Popover,
  Skeleton,
  Tag,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  SettingOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSubmittals, deleteSubmittal } from "../services/submittalsAPI";
import { useAuth } from "../hooks/useAuth";
import Highlighter from "react-highlight-words";
import { exportSubmittalsToPDF } from "../utils/pdfUtils";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import SortableHeader from "../components/SortableHeader";
import CreateShopDrawingModal from "../components/ShopDrawings/CreateShopDrawingModal";
import UpdateShopDrawingModal from "../components/ShopDrawings/UpdateShopDrawingModal";
import ViewShopDrawingModal from "../components/ShopDrawings/ViewShopDrawingModal";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";
import { formatDate } from "../utils/dateUtils";
import {
  applyDateFiltersToQueryParams,
  createGetColumnDateRangeProps,
} from "../utils/dateFilterUtils";

const SHOP_DRAWING_DATE_FILTER_KEYS = ["issue_date", "due_date"];

const buildShopDrawingFiltersForAPI = (filters) => ({
  ...applyDateFiltersToQueryParams(filters, SHOP_DRAWING_DATE_FILTER_KEYS),
  type: "Shop Drawing",
});

const ShopDrawings = () => {
  const {
    selectedProject,
    magicLoginActive,
    logout,
    currentUser,
    setCurrentUser,
  } = useAuth();
  const projectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name;
  const userRole = selectedProject?.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedSubmittalId, setSelectedSubmittalId] = useState(null);
  const queryClient = useQueryClient();
  const [searchedColumns, setSearchedColumns] = useState("");
  const searchInput = useRef(null);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize('shop_drawings'),
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const submittalId = searchParams.get("submittal_id");
  const isMagicLogin = localStorage.getItem("login_type") === "magic_login";
  const location = useLocation();
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchTexts, setSearchTexts] = useState({});
  const [masterSearch, setMasterSearch] = useState("");
  const baseVisibleColumns = [
    "formatted_spec_section",
    "formatted_number",
    "revision",
    "title",
    "formatted_received_from",
    "ball_in_court",
    "status",
    "priority",
  ];
  const initialVisible = currentUser?.submittal_columns || [
    ...baseVisibleColumns,
  ];
  const [visibleColumns, setVisibleColumns] = useState(initialVisible);
  const [tempSelection, setTempSelection] = useState(visibleColumns);
  const [tableKey, setTableKey] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const getColumnDateRangeProps = createGetColumnDateRangeProps(
    filters,
    setFilters,
  );

  const {
    data: submittalData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "submittals",
      projectId,
      pagination.current,
      pagination.pageSize,
      buildShopDrawingFiltersForAPI(filters),
      masterSearch,
      sortField,
      sortOrder,
    ],

    queryFn: () =>
      fetchSubmittals(
        projectId,
        pagination.current,
        pagination.pageSize,
        buildShopDrawingFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder
      ),
    enabled: !!projectId,
    keepPreviousData: true,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  }, [filters, masterSearch]);

  const columnSetMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: async (data) => {
      const user = await fetchCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      setCurrentUser(user);
      setIsPopoverOpen(false);
      setVisibleColumns(tempSelection);
      setIsLoadingColumns(false);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update columns",
        description: error.message,
      });
      setIsLoadingColumns(false);
    },
  });

  const handleSave = () => {
    setIsLoadingColumns(true);
    columnSetMutation.mutate({
      ...currentUser,
      submittal_columns: tempSelection,
    });
  };

  const handleCancel = () => {
    setTempSelection(visibleColumns);
    setIsPopoverOpen(false);
  };

  const handleExportPDF = async () => {
    try {
      if (!submittalData || submittalData.total === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({ 
        message: "Exporting PDF...", 
        description: "Fetching all records...",
        duration: 2
      });

      const allSubmittalsData = await fetchSubmittals(
        projectId,
        1,
        submittalData.total,
        buildShopDrawingFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
        true
      );

      await exportSubmittalsToPDF(
        allSubmittalsData.submittals,
        projectName,
        "Shop Drawings",
        { selectedProject },
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  useEffect(() => {
    const isMagic = magicLoginActive || isMagicLogin;
    if (
      isMagic &&
      location.pathname === "/submittals" &&
      !submittalId &&
      submittalData?.submittals?.length > 0
    ) {
      logout();
      navigate("/login");
    }
  }, [
    magicLoginActive,
    isMagicLogin,
    submittalId,
    submittalData,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (submittalId) {
      setSelectedSubmittalId(submittalId);
      setIsViewModalVisible(true);
      const next = new URLSearchParams(searchParams);
      next.delete("submittal_id");
      next.delete("project_id");
      setSearchParams(next, { replace: true });
    }
  }, [submittalId, searchParams, setSearchParams]);

  const deleteMutation = useMutation({
    mutationFn: deleteSubmittal,
    onSuccess: () => {
      queryClient.invalidateQueries(["submittals", projectId]);
      notification.success({ message: "Shop Drawing deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete shop drawing",
        description: error.message,
      });
    },
  });

  if (isError) {
    notification.error({
      message: "Failed to load shop drawings",
      description: error.message,
    });
  }

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, 'shop_drawings');
  };

  const handleUpdateClick = async (submittal) => {
    await setSelectedSubmittalId(submittal?.id);
    setIsUpdateModalVisible(true);
  };

  const handleViewClick = async (submittal) => {
    await setSelectedSubmittalId(submittal);
    setIsViewModalVisible(true);
  };

  const handleDeleteClick = (submittalId) => {
    deleteMutation.mutate(submittalId);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setSearchTexts((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
    setSearchTexts((prev) => {
      const updated = { ...prev };
      delete updated[dataIndex];
      return updated;
    });
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, confirm, dataIndex)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchTexts[dataIndex] || ""]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });


  const handleClearAllFilters = () => {
    setSearchedColumns([]);
    setSearchTexts({});
    setFilters({});
    setMasterSearch("");
    setSortField(null);
    setSortOrder("asc");
    setTableKey((prev) => prev + 1);
  };

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: (
        <SortableHeader
          title="Spec Section"
          sortField="formatted_spec_section"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "formatted_spec_section",
      key: "formatted_spec_section",
      isVisible: visibleColumns.includes("formatted_spec_section"),
      ...getColumnSearchProps("formatted_spec_section"),
    },
    {
      title: (
        <SortableHeader
          title="Number"
          sortField="formatted_number"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "formatted_number",
      key: "formatted_number",
      isVisible: visibleColumns.includes("formatted_number"),
      ...getColumnSearchProps("formatted_number"),
    },
    {
      title: (
        <SortableHeader
          title="Revision"
          sortField="revision"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "revision",
      key: "revision",
      isVisible: visibleColumns.includes("revision"),
      ...getColumnSearchProps("revision"),
    },
    {
      title: (
        <SortableHeader
          title="Title"
          sortField="title"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "title",
      key: "title",
      isVisible: visibleColumns.includes("title"),
      ...getColumnSearchProps("title"),
    },
    {
      title: "From",
      dataIndex: "formatted_received_from",
      key: "formatted_received_from",
      isVisible: visibleColumns.includes("formatted_received_from"),
      ...getColumnSearchProps("formatted_received_from"),
    },
    {
      title: "Ball in Court",
      dataIndex: "formatted_ball_in_courts",
      key: "ball_in_court",
      isVisible: visibleColumns.includes("ball_in_court"),
      ...getColumnSearchProps("formatted_ball_in_courts"),
    },
    {
      title: (
        <SortableHeader
          title="Issue Date"
          sortField="issue_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "issue_date",
      key: "issue_date",
      isVisible: visibleColumns.includes("issue_date"),
      ...getColumnDateRangeProps("issue_date", "Issue Date"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Due Date"
          sortField="due_date"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "due_date",
      key: "due_date",
      isVisible: visibleColumns.includes("due_date"),
      ...getColumnDateRangeProps("due_date", "Due Date"),
      render: (text) => formatDate(text),
    },
    {
      title: (
        <SortableHeader
          title="Status"
          sortField="status"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "status",
      key: "status",
      isVisible: visibleColumns.includes("status"),
      ...getColumnSearchProps("status"),
    },
    {
      title: (
        <SortableHeader
          title="Priority"
          sortField="priority"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "priority",
      key: "priority",
      isVisible: visibleColumns.includes("priority"),
      ...getColumnSearchProps("priority", "select", [
        { label: "High", value: "High" },
        { label: "Medium", value: "Medium" },
        { label: "Low", value: "Low" },
      ]),
      render: (priority) => {
        if (!priority) return <span>-</span>;

        const priorityColorMap = {
          High: "red",
          Medium: "blue",
          Low: "green",
        };

        const color = priorityColorMap[priority] || "default";

        return (
          <Tag color={color} style={{ fontWeight: "bold" }}>
            {priority}
          </Tag>
        );
      },
    },
    {
      title: (
        <SortableHeader
          title="EDMS"
          sortField="edms"
          currentSortField={sortField}
          currentSortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      dataIndex: "edms",
      key: "edms",
      isVisible: visibleColumns.includes("edms"),
      ...getColumnSearchProps("edms"),
    },
    {
      title: "Action",
      key: "action",
      isVisible: true,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="1" onClick={() => handleViewClick(record?.id)}>
                <Button className="w-full">View</Button>
              </Menu.Item>
              {isOwnerOrManager && (
                <>
                  <Menu.Item key="2" onClick={() => handleUpdateClick(record)}>
                    <Button className="w-full">Update</Button>
                  </Menu.Item>
                  <Menu.Item key="3">
                    <Popconfirm
                      title="Are you sure you want to delete this shop drawing?"
                      onConfirm={() => handleDeleteClick(record.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button className="w-full" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Menu.Item>
                </>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} size="medium"></Button>
        </Dropdown>
      ),
    },
  ];

  const content = (
    <div style={{ width: 200 }}>
      <Checkbox.Group
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
        value={tempSelection}
        onChange={setTempSelection}
      >
        {columns.filter((col) => col.key !== "action").map((col) => (
          <Checkbox key={col.key} value={col.key}>
            {col.title}
          </Checkbox>
        ))}
      </Checkbox.Group>
      <div className="flex justify-end mt-3 gap-2">
        <Button size="small" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="primary" size="small" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Shop Drawings</h1>
      <div className="flex gap-4 mb-4">
        {isOwnerOrManager && (
          <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
            Create
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportPDF}
          className="ml-2"
        >
          Export PDF
        </Button>

        <Popover
          content={isLoadingColumns ? <Skeleton active /> : content}
          title="Select Columns to Display"
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(visible) => setIsPopoverOpen(visible)}
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Popover>
        <Input.Search
          placeholder="Search across all fields..."
          defaultValue=""
          onSearch={(value) => setMasterSearch(value)}
          style={{ width: 400 }}
          allowClear
          enterButton={
            <Button
              type="primary"
              icon={<SearchOutlined />}
              className="!inline-flex !items-center !justify-center"
            />
          }
        />
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearAllFilters}
          className="ml-auto"
        >
          Clear All Filters
        </Button>
      </div>

      <Table
        columns={columns.filter((col) => col.isVisible === true)}
        dataSource={submittalData?.submittals}
        rowKey="id"
        key={tableKey}
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: submittalData?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <CreateShopDrawingModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />
      {selectedSubmittalId && (
        <UpdateShopDrawingModal
          visible={isUpdateModalVisible}
          onClose={() => {
            setIsUpdateModalVisible(false);
            setSelectedSubmittalId(null);
          }}
          submittalId={selectedSubmittalId}
        />
      )}
      {selectedSubmittalId && (
        <ViewShopDrawingModal
          visible={isViewModalVisible}
          onClose={
            magicLoginActive || isMagicLogin
              ? () => {}
              : () => setIsViewModalVisible(false)
          }
          submittalId={selectedSubmittalId}
        />
      )}
    </div>
  );
};

export default ShopDrawings;
